import { Button, Card, Typography } from "@/shared/components/ui";
import {
  CardContent,
  CardFooter,
  CardTitle,
  CardHeader,
} from "@/shared/components/ui/card";
import { Separator } from "@/shared/components/ui/separator";
import { Slider } from "@/shared/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getDashboardLink } from "@/shared/utils/link";
import { ROUTES } from "@/core/router/routes";
import useCurrentTokenBalance from "./useCurrentTokenBalance";

interface IUploadCreditCardProps {
  isHeader?: boolean;
}

export default function UploadCreditCard({
  isHeader = false,
}: IUploadCreditCardProps) {
  const { t } = useTranslation();
  const { currentTokenBalanceFormatted } = useCurrentTokenBalance();
  const [creditValue, setCreditValue] = useState(1000);
  const [totalPrice, setTotalPrice] = useState(2);
  const [totalPriceFocused, setTotalPriceFocused] = useState(false);
  const totalPriceFormatted = totalPriceFocused
    ? totalPrice.toString()
    : `$${totalPrice.toFixed(2)}`;
  return (
    <Card className="rounded-[16px] h-[295px]">
      <CardHeader className="flex flex-row justify-between items-center h-[56px]">
        <CardTitle className="default text-[15px] font-700">
          {t("setting:upload-credit")}
        </CardTitle>

        {isHeader ? (
          <Link
            className="cursor-pointer"
            to={`${getDashboardLink(
              ROUTES.Dashboard.Setting.PlansAndBillings
            )}`}
          >
            <div className="p-2.5 !mt-0 rounded-[100px] outline outline-1 outline-offset-[-1px] outline-border flex justify-start items-center gap-2.5">
              <div className="w-4 h-4 relative overflow-hidden">
                <div className="">
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.1665 4.66602H11.8332M11.8332 4.66602V11.3327M11.8332 4.66602L5.1665 11.3327"
                      stroke="#71717A"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </Link>
        ) : (
          <div
            className="flex flex-row gap-2 items-center justify-center rounded-[6px] px-[8px] py-[6px]"
            style={{
              background: "rgba(228, 228, 231, 0.32)",
            }}
          >
            <svg
              width="20"
              height="15"
              viewBox="0 0 20 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.3853 4.49814V4.06299C14.3853 2.10361 11.4298 0.625488 7.51028 0.625488C3.59075 0.625488 0.635284 2.10361 0.635284 4.06299V7.18799C0.635284 8.82002 2.68607 10.1169 5.63528 10.5052V10.938C5.63528 12.8974 8.59075 14.3755 12.5103 14.3755C16.4298 14.3755 19.3853 12.8974 19.3853 10.938V7.81299C19.3853 6.1958 17.3993 4.89736 14.3853 4.49814ZM18.1353 7.81299C18.1353 8.8458 15.7298 10.0005 12.5103 10.0005C12.2189 10.0005 11.9298 9.99033 11.6447 9.97158C13.3298 9.35752 14.3853 8.35986 14.3853 7.18799V5.76143C16.7189 6.10908 18.1353 7.05283 18.1353 7.81299ZM5.63528 9.23877V7.38018C6.25699 7.46118 6.88333 7.50137 7.51028 7.50049C8.13724 7.50137 8.76358 7.46118 9.38528 7.38018V9.23877C8.76451 9.33048 8.1378 9.37618 7.51028 9.37549C6.88277 9.37618 6.25606 9.33048 5.63528 9.23877ZM13.1353 6.08877V7.18799C13.1353 7.84346 12.1658 8.54736 10.6353 8.97471V7.14893C11.6439 6.90439 12.4978 6.54033 13.1353 6.08877ZM7.51028 1.87549C10.7298 1.87549 13.1353 3.03018 13.1353 4.06299C13.1353 5.0958 10.7298 6.25049 7.51028 6.25049C4.29075 6.25049 1.88528 5.0958 1.88528 4.06299C1.88528 3.03018 4.29075 1.87549 7.51028 1.87549ZM1.88528 7.18799V6.08877C2.52278 6.54033 3.37669 6.90439 4.38528 7.14893V8.97471C2.85482 8.54736 1.88528 7.84346 1.88528 7.18799ZM6.88528 10.938V10.6122C7.09075 10.62 7.29857 10.6255 7.51028 10.6255C7.81341 10.6255 8.1095 10.6153 8.40013 10.5981C8.72299 10.7137 9.05185 10.8118 9.38528 10.8919V12.7247C7.85482 12.2974 6.88528 11.5935 6.88528 10.938ZM10.6353 12.9888V11.1255C11.2568 11.209 11.8832 11.2508 12.5103 11.2505C13.1372 11.2514 13.7636 11.2112 14.3853 11.1302V12.9888C13.1419 13.1711 11.8786 13.1711 10.6353 12.9888ZM15.6353 12.7247V10.8989C16.6439 10.6544 17.4978 10.2903 18.1353 9.83877V10.938C18.1353 11.5935 17.1658 12.2974 15.6353 12.7247Z"
                fill="#1F2223"
              />
            </svg>
            <Tooltip>
              <TooltipTrigger>
                <Typography className="flex flex-row gap-1 text-[14px] font-500 leading-[24px] text-[#000000]">
                  {currentTokenBalanceFormatted}
                </Typography>
                <TooltipContent>
                  {t("setting:remaining-monthly-credits")}
                </TooltipContent>
              </TooltipTrigger>
            </Tooltip>
          </div>
        )}
      </CardHeader>
      <Separator />
      <CardContent className="flex flex-col h-[150px] pt-[16px]">
        <div className="flex flex-row justify-between w-full">
          <div className="w-full flex flex-col gap-1 items-center">
            <Typography className="text-[12px] font-500 leading-[24px] text-muted-foreground">
              {t("setting:credit-value")}
            </Typography>
            <input
              type="number"
              value={creditValue}
              onChange={(e) => {
                const newValue = parseInt(e.target.value) || 0;
                const clampedValue = Math.min(Math.max(newValue, 0), 100000);
                const roundedValue = Math.round(clampedValue / 1000) * 1000;
                setCreditValue(roundedValue);
                setTotalPrice(roundedValue * 0.002);
              }}
              className="text-[36px] !font-600 text-zinc-950 bg-transparent border-none outline-none text-center w-full [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
              min="0"
              max="100000"
              step="1000"
            />
          </div>
          <div className="w-full flex flex-col gap-1 items-center">
            <Typography className="text-[12px] font-500 leading-[24px] text-muted-foreground">
              {t("setting:total-price")}
            </Typography>
            <input
              type="text"
              value={totalPriceFormatted}
              onChange={(e) => {
                const numericValue = e.target.value.replace("$", "");
                const newValue = parseFloat(numericValue) || 0;
                const clampedValue = Math.min(Math.max(newValue, 0), 200);
                setTotalPrice(clampedValue);
                const calculatedCredit = Math.round(clampedValue * 500);
                const roundedCredit =
                  Math.round(calculatedCredit / 1000) * 1000;
                setCreditValue(roundedCredit);
              }}
              onFocus={() => {
                setTotalPriceFocused(true);
              }}
              onBlur={() => {
                setTotalPriceFocused(false);
              }}
              className="text-[36px] !font-600 text-zinc-950 bg-transparent border-none outline-none text-center w-full"
            />
          </div>
        </div>
        <div className="w-full mt-4">
          <Slider
            value={[creditValue]}
            className="h-[7px] rounded-[10px] bg-zinc-200"
            min={0}
            max={100000}
            progressColor="#22C55E"
            thumbClassName="w-[30px] h-[30px] border-zinc-200"
            thumbIcon={
              <div className="flex flex-row items-center justify-center w-full h-full cursor-pointer">
                <ChevronLeft className="w-[30px] h-[30px] text-[#22C55E] -mr-[4px]" />
                <ChevronRight className="w-[30px] h-[30px] text-[#22C55E] -ml-[4px]" />
              </div>
            }
            step={1000}
            onValueChange={(value) => {
              const roundedValue = Math.round(value[0] / 1000) * 1000;
              setCreditValue(roundedValue);
              setTotalPrice(roundedValue * 0.002);
            }}
          />
          <div className="flex flex-row gap-2 items-center justify-between mt-1">
            <span className="text-[12px] font-500 leading-[24px] text-muted-foreground">
              0
            </span>
            <span className="text-[12px] font-500 leading-[24px] text-muted-foreground">
              100000
            </span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-2 items-start">
        <Button className="w-full"> {t("setting:continue-to-payment")}</Button>
        <span className="text-[13px] font-600 text-muted-foreground">
          {t("setting:credit-value-description")}
        </span>
      </CardFooter>
    </Card>
  );
}
